/**
 * F-505-FE-04: AdminLayout
 *
 * Desktop-first layout for the Central Admin Portal:
 * - Persistent top navigation bar (Dashboard, Languages, Specializations, Templates)
 * - Breadcrumb navigation for hierarchical pages
 * - Account menu with logout in top bar
 * - Responsive sidebar collapse for smaller screens (nav collapses to hamburger)
 *
 * Design: Bloomberg Terminal meets modern design system — data-dense but elegant.
 * Substantial, professional header. Editorial whitespace in content area.
 */

import {
  type ReactNode,
  useCallback,
  useState,
} from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface AdminLayoutProps {
  children: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
}

// ---------------------------------------------------------------------------
// Nav items
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { to: '/admin/dashboard', label: 'Dashboard' },
  { to: '/admin/languages', label: 'Languages' },
  { to: '/admin/specializations', label: 'Specializations' },
  { to: '/admin/templates', label: 'Templates' },
  { to: '/admin/settings', label: 'Settings' },
] as const;

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function MenuIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className ?? 'w-4 h-4'}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  if (!items.length) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm font-body">
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <span key={idx} className="flex items-center gap-1.5">
            {idx > 0 && (
              <ChevronRightIcon className="w-3.5 h-3.5 text-clinical-text-muted flex-none" />
            )}
            {isLast || !item.to ? (
              <span
                className={
                  isLast
                    ? 'text-clinical-charcoal font-semibold'
                    : 'text-clinical-text-muted'
                }
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            ) : (
              <Link
                to={item.to}
                className="text-clinical-text-muted hover:text-clinical-charcoal transition-colors"
              >
                {item.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Account Menu
// ---------------------------------------------------------------------------

function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/admin/login');
  }, [logout, navigate]);

  const displayEmail = user?.email ?? 'Admin';

  return (
    <div className="relative group">
      <button
        type="button"
        className="
          inline-flex items-center gap-2 min-h-[44px] px-3 rounded-lg
          text-sm font-body font-medium text-white/80
          hover:text-white hover:bg-white/10
          transition-colors duration-150
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2 focus-visible:ring-offset-clinical-charcoal
        "
        aria-label="Account menu"
        aria-haspopup="menu"
      >
        <ShieldIcon />
        <span className="hidden sm:inline max-w-[140px] truncate">{displayEmail}</span>
        <svg className="w-4 h-4 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      <div
        className="
          absolute end-0 top-full mt-2 z-50
          min-w-[200px]
          bg-white rounded-clinical shadow-clinical-lg border border-clinical-border
          opacity-0 invisible
          group-hover:opacity-100 group-hover:visible
          group-focus-within:opacity-100 group-focus-within:visible
          transition-all duration-150
        "
        role="menu"
      >
        <div className="px-4 py-3 border-b border-clinical-border">
          <p className="text-xs font-body text-clinical-text-muted uppercase tracking-wide">
            Central Admin
          </p>
          <p className="text-sm font-body font-semibold text-clinical-charcoal truncate mt-0.5">
            {displayEmail}
          </p>
        </div>
        <button
          type="button"
          role="menuitem"
          onClick={() => { void handleLogout(); }}
          className="
            w-full text-start px-4 py-3
            text-sm font-body text-clinical-charcoal
            hover:bg-clinical-cream hover:text-clinical-teal
            transition-colors duration-150
            rounded-b-clinical
          "
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile nav drawer
// ---------------------------------------------------------------------------

function MobileNavDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-clinical-charcoal/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <div
        className="
          fixed inset-y-0 start-0 z-50 w-64
          bg-clinical-charcoal
          shadow-clinical-lg
          flex flex-col
          transition-transform duration-200
        "
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
          <span className="font-heading text-base font-semibold text-white">
            Admin Portal
          </span>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close menu"
          >
            <XIcon />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1" role="navigation" aria-label="Admin navigation">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) => [
                'flex items-center min-h-[44px] px-3 rounded-lg text-sm font-body font-medium transition-colors duration-150',
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10',
              ].join(' ')}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Layout
// ---------------------------------------------------------------------------

export function AdminLayout({ children, breadcrumbs = [] }: AdminLayoutProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] font-body">
      {/* ------------------------------------------------------------------ */}
      {/* Top navigation bar — dark, authoritative                            */}
      {/* ------------------------------------------------------------------ */}
      <header
        className="
          sticky top-0 z-40
          bg-clinical-charcoal
          border-b border-white/10
        "
        style={{ boxShadow: '0 2px 8px 0 rgba(26,26,46,0.20)' }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center gap-6">
            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="
                lg:hidden
                inline-flex items-center justify-center min-h-[44px] min-w-[44px]
                rounded-lg text-white/70 hover:text-white hover:bg-white/10
                transition-colors duration-150
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber
              "
              aria-label="Open navigation menu"
              aria-expanded={mobileNavOpen}
            >
              <MenuIcon />
            </button>

            {/* Wordmark */}
            <Link
              to="/admin/dashboard"
              className="font-heading text-base font-semibold text-white hover:text-clinical-amber transition-colors flex-none"
              aria-label="Clinical Interpreter Admin — go to dashboard"
            >
              <span className="hidden sm:inline">Clinical Interpreter</span>
              <span className="sm:hidden">CI Admin</span>
              <span className="ms-2 text-xs font-body font-normal text-clinical-amber/70 uppercase tracking-widest">
                Admin
              </span>
            </Link>

            {/* Desktop nav links */}
            <nav
              className="hidden lg:flex items-center gap-1 flex-1"
              role="navigation"
              aria-label="Admin navigation"
            >
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => [
                    'inline-flex items-center min-h-[44px] px-3.5 rounded-lg text-sm font-body font-medium transition-colors duration-150',
                    isActive
                      ? 'bg-white/15 text-white'
                      : 'text-white/65 hover:text-white hover:bg-white/10',
                  ].join(' ')}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* Account menu */}
            <div className="ms-auto flex-none">
              <AccountMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Breadcrumb bar (conditional)                                        */}
      {/* ------------------------------------------------------------------ */}
      {breadcrumbs.length > 0 && (
        <div className="border-b border-clinical-border bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-10">
              <Breadcrumbs items={breadcrumbs} />
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Page content                                                         */}
      {/* ------------------------------------------------------------------ */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
}
